import { ReactComponent as ArrowDown } from 'icons/arrow-down.svg';
import React, { ChangeEvent, FunctionComponent, KeyboardEvent, SelectHTMLAttributes } from 'react';
import styled from 'styled-components';
import { CaptionElementProps } from './types';
import { getYearOptions } from './utils';

export interface IMonthYearCaption extends CaptionElementProps {
  onMonthChange: (month: Date) => void;
}

/**
 * Month and year dropdowns in place of the calendar's month title. Rendered through
 * `captionElement`, which injects `date` - the month currently on screen.
 */
const MonthYearCaption: FunctionComponent<IMonthYearCaption> = ({
  classNames,
  date,
  locale,
  localeUtils,
  months,
  onMonthChange,
}) => {
  const displayedYear = date.getFullYear();
  const monthNames = months || localeUtils.getMonths(locale);

  const handleMonthSelect = (e: ChangeEvent<HTMLSelectElement>) =>
    onMonthChange(new Date(displayedYear, Number(e.target.value)));

  const handleYearSelect = (e: ChangeEvent<HTMLSelectElement>) =>
    onMonthChange(new Date(Number(e.target.value), date.getMonth()));

  /**
   * The picker cancels the arrow keys on its wrapper to page the calendar, which would
   * take them from an open dropdown, and Enter would submit the surrounding form. Escape
   * bubbles on, closing the popup through the picker's own handler.
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      return;
    }

    e.stopPropagation();

    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <div className={classNames.caption} onKeyDown={handleKeyDown}>
      <Select
        aria-label="Choose the Month"
        autoComplete="off"
        name="months"
        onChange={handleMonthSelect}
        value={date.getMonth()}
      >
        {monthNames.map((monthName, index) => (
          <option key={monthName} value={index}>
            {monthName}
          </option>
        ))}
      </Select>
      <Select
        aria-label="Choose the Year"
        autoComplete="off"
        name="years"
        onChange={handleYearSelect}
        value={displayedYear}
      >
        {getYearOptions(displayedYear).map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </Select>
    </div>
  );
};

export default MonthYearCaption;

const Select: FunctionComponent<SelectHTMLAttributes<HTMLSelectElement>> = ({
  children,
  ...props
}) => (
  <SelectWrapper>
    <NativeSelect {...props}>{children}</NativeSelect>
    <Arrow />
  </SelectWrapper>
);

const SelectWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  & + & {
    margin-left: 8px;
  }
`;

const Arrow = styled(ArrowDown)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  margin: auto 0;
  width: 1.5em;
  height: 1.5em;
  pointer-events: none;
  path {
    fill: ${(props) => props.theme.colors.darkGrey};
  }
`;

const NativeSelect = styled.select`
  appearance: none;
  padding: 4px 1.75em 4px 10px;
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 4px;
  background: ${(props) => props.theme.colors.snow};
  transition: all 0.3s;
  color: ${(props) => props.theme.colors.dark};
  font-weight: 500;
  font-size: 1em;
  font-family: inherit;
  cursor: pointer;
  &:hover {
    border-color: #8b9898;
  }
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.focusBorder};
  }
`;
